import React, { useEffect, useState } from 'react'
import { styled } from '@mui/material';
import { Dialog, DialogTitle } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { addInterpreterLunch } from 'slices/citySlice';
import { fetchInterpreters } from 'slices/interpreterSlice';
import toast from "react-hot-toast";
import ErrorMessage from 'components/common/ErrorMessage';
import moment from 'moment';
import Api from 'services/api';
import { useDispatch } from 'react-redux';
import { AsyncPaginate } from 'react-select-async-paginate';
import { selectCustomStyles } from 'components/common/Select';

export const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-container': {
    overflowY: "auto",
    margin: "auto",
    alignItems: "flex-start",
  },
  '& .MuiPaper-root': {
    maxHeight: "none",
    overflowY: "hidden",
    borderRadius: 20
  },
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
    backgroundColor: "red"
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
    backgroundColor: "red"
  },
}));

const AddLunchSheduleModal = ({ item, onClose, show, isAdmin = false }) => {
  const dispatch = useDispatch();
  const [selectedInterpreter, setSelectedInterpreter] = useState(null);

  const methods = useForm({
    defaultValues: {
      start_date: moment().format("YYYY-MM-DD"),
      end_date: moment().format("YYYY-MM-DD"),
      start_time: "",
      end_time: "",
    }
  });

  const { control, setValue, handleSubmit, formState: { errors }, setError, clearErrors } = methods;

  useEffect(() => {
    if (show) {
      setSelectedInterpreter(null);
      if (!isAdmin) {
        Api.fetchInterpreterProfile().then((res) => {
          if (res?.status >= 200 && res?.status < 300) {
            const profile = res.data?.data;
            setValue("start_time", profile?.lunch_start_time || "");
            setValue("end_time", profile?.lunch_end_time || "");
          }
        });
      }
    }
  }, [show, setValue, isAdmin]);

  const loadInterpreters = async (inputValue, loadedOptions, { page }) => {
    const result = await dispatch(
      fetchInterpreters(page, { pageLength: 20, sortBy: "id", sortDirection: "asc", search: inputValue }, true, true)
    );
    return {
      options: (result?.data || []).map((v) => ({
        value: v.id,
        label: [v.first_name, v.last_name].filter(Boolean).join(' '),
      })),
      hasMore: result?.last_page ? page < result.last_page : false,
      additional: { page: page + 1 },
    };
  };

  const onSubmit = async (data) => {
    if (isAdmin) {
      if (!selectedInterpreter) {
        setError("interpreter_id", { message: "Please select an interpreter" });
        return;
      }
      try {
        const res = await Api.adminSetInterpreterLunch(selectedInterpreter.value, data);
        if (res?.status >= 200 && res?.status < 300) {
          toast.success(res.data?.message || 'Lunch schedule updated successfully.', { className: "successToast" });
          onClose();
        } else {
          toast.error(res?.data?.message || 'Something went wrong', { className: "errorToast" });
        }
      } catch {
        toast.error('Server error', { className: "errorToast" });
      }
    } else {
      dispatch(addInterpreterLunch(data, (success, message) => {
        if (success) {
          onClose();
          toast.success(message, { className: "successToast" });
        } else {
          toast.error(message || "Something went wrong", { className: "errorToast" });
        }
      }));
    }
  };

  const handleClear = () => {
    if (isAdmin) {
      if (!selectedInterpreter) {
        setError("interpreter_id", { message: "Please select an interpreter" });
        return;
      }
      Api.adminSetInterpreterLunch(selectedInterpreter.value, { start_time: null, end_time: null, start_date: null, end_date: null })
        .then((res) => {
          if (res?.status >= 200 && res?.status < 300) {
            toast.success("Schedule cleared successfully!", { className: "successToast" });
            onClose();
          } else {
            toast.error(res?.data?.message || "Something went wrong", { className: "errorToast" });
          }
        });
    } else {
      dispatch(addInterpreterLunch({ start_time: null, end_time: null }, (success, message) => {
        if (success) {
          toast.success("Schedule cleared successfully!", { className: "successToast" });
          onClose();
        } else {
          toast.error(message || "Something went wrong", { className: "errorToast" });
        }
      }));
    }
  };

  return (
    <BootstrapDialog
      fullWidth
      maxWidth="md"
      onClose={onClose}
      open={show}
      sx={{
        '& .MuiDialog-container': { alignItems: "center" },
        '& .MuiPaper-root': { overflowY: "visible" }
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="modal-content full_modal">
          <DialogTitle>
            <div className="modal-header header_modal align-items-center">
              <div></div>
              <h5 className="modal-title_u">
                Lunch Schedule
              </h5>
              <button type="button" className="btn-close" aria-label="Close"
                onClick={() => onClose(item?.id, item?.status)} />
            </div>
          </DialogTitle>

          <div className="modal-body modal_form_body">

            {/* Interpreter dropdown — admin only */}
            {isAdmin && (
              <div style={{ marginBottom: 16 }}>
                <h2>Interpreter *</h2>
                <AsyncPaginate
                  menuPlacement="auto"
                  styles={selectCustomStyles}
                  value={selectedInterpreter}
                  loadOptions={loadInterpreters}
                  isClearable
                  onChange={(obj) => {
                    setSelectedInterpreter(obj || null);
                    clearErrors("interpreter_id");
                  }}
                  placeholder="Select interpreter..."
                  additional={{ page: 1 }}
                />
                {errors?.interpreter_id?.message && (
                  <ErrorMessage text={errors.interpreter_id.message} />
                )}
              </div>
            )}

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px 24px" }}>

              {/* Start Date */}
              <div>
                <h2>Start Date*</h2>
                <Controller
                  name="start_date"
                  control={control}
                  rules={{ required: "Please select a start date" }}
                  render={({ field }) => (
                    <div style={{ width: "100%" }}>
                      <div className="input-image">
                        <input
                          type="date"
                          {...field}
                          value={field.value || ""}
                          onChange={(e) => field.onChange(e.target.value)}
                        />
                        <img src="/images/celender.png" alt="calendar" className="custom-icon" />
                      </div>
                      {errors?.start_date?.message && (
                        <ErrorMessage text={errors.start_date.message} />
                      )}
                    </div>
                  )}
                />
              </div>

              {/* End Date */}
              <div>
                <h2>End Date*</h2>
                <Controller
                  name="end_date"
                  control={control}
                  rules={{ required: "Please select an end date" }}
                  render={({ field }) => (
                    <div style={{ width: "100%" }}>
                      <div className="input-image">
                        <input
                          type="date"
                          {...field}
                          value={field.value || ""}
                          onChange={(e) => field.onChange(e.target.value)}
                        />
                        <img src="/images/celender.png" alt="calendar" className="custom-icon" />
                      </div>
                      {errors?.end_date?.message && (
                        <ErrorMessage text={errors.end_date.message} />
                      )}
                    </div>
                  )}
                />
              </div>

              {/* Start Time */}
              <div>
                <h2>Start Time*</h2>
                <Controller
                  name="start_time"
                  control={control}
                  rules={{ required: "Please select start time" }}
                  render={({ field }) => (
                    <div style={{ width: "100%" }}>
                      <div className="input-image">
                        <input
                          type="time"
                          {...field}
                          value={field.value || ""}
                          onChange={(e) => {
                            field.onChange(e.target.value);
                            const end_time = e.target.value
                              ? moment(e.target.value, "HH:mm").add(30, "minutes").format("HH:mm")
                              : "";
                            setValue("end_time", end_time);
                          }}
                        />
                        <img src="/images/clock.png" alt="clock" className="custom-icon" />
                      </div>
                      {errors?.start_time?.message && (
                        <ErrorMessage text={errors.start_time.message} />
                      )}
                    </div>
                  )}
                />
              </div>

              {/* End Time */}
              <div>
                <h2>End Time*</h2>
                <Controller
                  name="end_time"
                  control={control}
                  rules={{ required: "Please select end time" }}
                  render={({ field }) => (
                    <div style={{ width: "100%" }}>
                      <div className="input-image">
                        <input
                          type="time"
                          {...field}
                          value={field.value || ""}
                          onChange={(e) => field.onChange(e.target.value)}
                        />
                        <img src="/images/clock.png" alt="clock" className="custom-icon" />
                      </div>
                      {errors?.end_time?.message && (
                        <ErrorMessage text={errors.end_time.message} />
                      )}
                    </div>
                  )}
                />
              </div>

            </div>

            <div style={{ marginTop: 20, textAlign: "right" }}>
              <button
                type="button"
                className="main_button Cancle_button"
                style={{ marginRight: 10 }}
                onClick={handleClear}
              >
                Clear
              </button>
              <button type="submit" className="main_button">
                Save Schedule
              </button>
            </div>
          </div>
        </div>
      </form>
    </BootstrapDialog>
  );
};

export default AddLunchSheduleModal;
